"use client";

import {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import type {BusinessContact} from "@/lib/user/contacts";
import {outreachCopy, outreachRequest, type OutreachTemplate} from "@/lib/user/outreach";
import {OutreachSendConfirmation,type OutreachSendBatch} from "@/components/outreach-send-confirmation";
import {emailQuotaError,type EmailQuota} from "@/lib/user/email-quota";

/** 触达编辑器保留未保存文案；发送前只检查发件配置，服务端未开放真实发送时绝不调用发送接口。 */
export function OutreachComposer({locale, contacts, templates, initial, savedForm, onFormChange, onClose, onSaved}: {locale: string; contacts: BusinessContact[]; templates: OutreachTemplate[]; initial?: OutreachTemplate; savedForm?: OutreachTemplate; onFormChange?: (form: OutreachTemplate) => void; onClose: () => void; onSaved: () => void}) {
  const copy = outreachCopy(locale);
  const [form, setForm] = useState<OutreachTemplate>(savedForm ?? initial ?? {name: "", channel: "email", language: locale, tone: "professional", subject: "", body: ""});
  const formRef = useRef(form);
  const [templateID, setTemplateID] = useState(initial?.id ?? "");
  const [prompt, setPrompt] = useState("");
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState("");
  const [sendBatch,setSendBatch]=useState<OutreachSendBatch|null>(null);
  const[quota,setQuota]=useState<EmailQuota>();
  const sending=useRef<{fingerprint:string;key:string}|null>(null);
  const [preview, setPreview] = useState(false);
  const [recipient, setRecipient] = useState(contacts[0]?.id ?? "");
  const dialog = useRef<HTMLElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const generation = useRef<{fingerprint: string; key: string} | null>(null);

  useEffect(() => {
    const focused = document.activeElement as HTMLElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    close.current?.focus();
    return () => { document.body.style.overflow = overflow; focused?.focus(); };
  }, []);

  function updateForm(next: OutreachTemplate) {
    // 父组件保留编辑快照不能发生在 React 状态更新函数中；ref 保留连续编辑时的最新表单。
    formRef.current = next;
    setForm(next);
    onFormChange?.(next);
  }

  function change(key: keyof OutreachTemplate, value: string) {
    updateForm({...formRef.current, [key]: value});
    if (key === "channel") setTemplateID("");
    generation.current = null;
    setNotice("");
  }

  async function action(kind: "generate" | "template") {
    if (pending) return;
    setPending(true);
    setNotice("");
    try {
      if (kind === "generate") {
        const params = {channel: form.channel, tone: form.tone, prompt, fallback_language: locale};
        const fingerprint = JSON.stringify(params);
        if (generation.current?.fingerprint !== fingerprint) generation.current = {fingerprint, key: crypto.randomUUID()};
        const result = await outreachRequest<{template: OutreachTemplate}>("generate", params, generation.current.key);
        updateForm({...formRef.current, language: result.template.language, subject: result.template.subject, body: result.template.body});
        setTemplateID("");
      } else {
        await outreachRequest("templates", {name: form.name, channel: form.channel, language: form.language, tone: form.tone, subject: form.subject, body: form.body});
        setNotice(copy.saved);
        onSaved();
      }
    } catch {
      setNotice(copy.failed);
    } finally {
      setPending(false);
    }
  }

  async function checkSender() {
    if (pending) return;
    let currentQuota=quota;
    setPending(true);
    setNotice("");
    try {
      if (form.channel!=="email") {setNotice(copy.sendingUnavailable);return;}
      const params={channel:form.channel,language:form.language,tone:form.tone,subject:form.subject,body:form.body,contact_ids:contacts.map(c=>c.id)};
      const fingerprint=JSON.stringify(params);
      if(sending.current?.fingerprint!==fingerprint)sending.current={fingerprint,key:crypto.randomUUID()};
      const config = await outreachRequest<{sending_enabled: boolean; reason: string;quota?:EmailQuota}>("config");
      setQuota(config.quota);currentQuota=config.quota;
      if(!config.sending_enabled){setNotice(config.reason==="sender_not_configured"?copy.senderRequiredDetail:emailQuotaError(config.reason,locale,config.quota)??copy.sendingUnavailable);return;}
      const result=await outreachRequest<{batch:OutreachSendBatch}>("prepare",params,sending.current.key);
      setSendBatch(result.batch);
    } catch (problem) {
      setNotice(emailQuotaError(problem instanceof Error?problem.message:"",locale,currentQuota)??copy.failed);
    } finally {
      setPending(false);
    }
  }

  const selected = contacts.find(contact => contact.id === recipient) ?? contacts[0];
  const render = (text: string) => text.replace(/\{\{\s*business_name\s*\}\}/g, selected?.name ?? "{{business_name}}");
  const valid = Boolean(form.body.trim() && (form.channel !== "email" || form.subject.trim()));

  function keys(event: React.KeyboardEvent) {
    if (sendBatch) return;
    if (event.key === "Escape" && !pending) { event.stopPropagation(); onClose(); }
    if (event.key !== "Tab") return;
    const elements = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled)') ?? [])
      .filter(element => element.getClientRects().length > 0 && getComputedStyle(element).visibility !== "hidden");
    if (event.shiftKey && document.activeElement === elements[0]) { event.preventDefault(); elements.at(-1)?.focus(); }
    else if (!event.shiftKey && document.activeElement === elements.at(-1)) { event.preventDefault(); elements[0]?.focus(); }
  }

  return createPortal(
    <div className="outreach-backdrop">
      <section className="outreach-dialog" role="dialog" aria-modal="true" aria-label={copy.compose} ref={dialog} onKeyDown={keys}>
        <header inert={Boolean(sendBatch)}>
          <h2>{copy.compose}{contacts.length > 0 ? ` (${contacts.length})` : ""}</h2>
          <button ref={close} type="button" aria-label={copy.close} onClick={onClose} disabled={pending}>×</button>
        </header>
        <div className="outreach-dialog-content" inert={Boolean(sendBatch)}>
          <div className="outreach-settings">
            <div className="outreach-form-grid">
              <label>{copy.channel}<select value={form.channel} disabled={pending} onChange={event => change("channel", event.target.value)}><option value="email">{copy.email}</option><option value="sms">{copy.sms}</option><option value="whatsapp">{copy.whatsapp}</option></select></label>
              <label>{copy.tone}<select value={form.tone} disabled={pending} onChange={event => change("tone", event.target.value)}>{[["professional", copy.professional], ["friendly", copy.friendly], ["concise", copy.concise]].map(([value, title]) => <option key={value} value={value}>{title}</option>)}</select></label>
            </div>
            <label className="outreach-field">{copy.template}<select value={templateID} disabled={pending} onChange={event => { const template = templates.find(item => item.id === event.target.value); setTemplateID(event.target.value); if (template) updateForm({...template}); }}><option value="">{copy.custom}</option>{templates.filter(item => item.channel === form.channel).map(item => <option key={item.id} value={item.id}>{item.name} · {item.language}</option>)}</select></label>
            <label className="outreach-field outreach-prompt">{copy.prompt}<textarea value={prompt} maxLength={5000} rows={7} placeholder={copy.promptPlaceholder} onChange={event => { setPrompt(event.target.value); generation.current = null; setNotice(""); }}/></label>
            <div className="outreach-inline-actions"><button type="button" disabled={pending || !prompt.trim()} onClick={() => void action("generate")}>{pending ? copy.pending : copy.generate}</button></div>
          </div>
          <div className="outreach-editor">
            <label className="outreach-field">{copy.name}<input value={form.name} maxLength={200} onChange={event => change("name", event.target.value)}/></label>
            {form.channel === "email" && <label className="outreach-field">{copy.subject}<input value={form.subject} maxLength={500} onChange={event => change("subject", event.target.value)}/></label>}
            <label className="outreach-field outreach-message">{copy.body}<textarea value={form.body} maxLength={20000} rows={12} onChange={event => change("body", event.target.value)}/></label>
            <div className="outreach-inline-actions">
              <button type="button" disabled={pending} onClick={() => change("body", form.body + "{{business_name}}")}>{copy.variable}</button>
              {contacts.length > 0 && <><button type="button" onClick={() => setPreview(value => !value)} aria-expanded={preview}>{copy.preview}</button><select aria-label={copy.selected} value={recipient} onChange={event => setRecipient(event.target.value)}>{contacts.map(contact => <option key={contact.id} value={contact.id}>{contact.name}</option>)}</select></>}
            </div>
            {preview && selected && <section className="outreach-preview"><h3>{selected.name}</h3>{form.channel === "email" && <strong>{render(form.subject)}</strong>}<p>{render(form.body)}</p></section>}
            {notice && <p role="alert">{notice}</p>}
          </div>
        </div>
        <footer inert={Boolean(sendBatch)}>
          <button type="button" disabled={pending || !valid || !form.name.trim()} onClick={() => void action("template")}>{copy.saveTemplate}</button>
          {contacts.length > 0 && <button type="button" disabled={pending || !valid} onClick={() => void checkSender()}>{copy.send}</button>}
        </footer>
        {sendBatch&&<OutreachSendConfirmation initial={sendBatch} locale={locale} onClose={()=>{if(sendBatch.state==="prepared"&&Date.now()>=new Date(sendBatch.expires_at).getTime())sending.current=null;setSendBatch(null);}} onSent={onSaved}/>}
      </section>
    </div>, document.body,
  );
}
